import React from 'react';
import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import moment from 'moment';
import { ErpOrder } from '../../models/ErpOrder';
import ServicesContext from '../../contexts/ServicesContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../common-components/Button/Button';
import { Field, FieldType } from '../../common-components/Field/Field';
import { WaitingBox } from '../../common-components/WaitingBox/WaitingBox';
import { GetErpOrderNFeInput } from '../../services/Orders/GetErpOrderNFe/GetErpOrderNFeInput';
import { HistoryByOrder } from '../../services/Orders/GetHistoryByOrder/HistoryByOrder';
import { NfBlock, Title, CreatedAt, Status, LeftContainer, RightContainer, Details, FooterContent, ListItem, HistoryBlock } from './styles';
import { strings } from '../../resources/strings';
import { ProfileType } from '../../enums/ProfileType';

interface IModalListNFesProps {
  visible: boolean;
  close: (orderId?: number) => void;
  title: string;
  orderId?: number;
}

const ModalListNFes: React.FC<IModalListNFesProps> = ({
  visible,
  close,
  title,
  orderId,
}) => {

  const { session } = useAuth();
  const [erpOrder, setErpOrder] = React.useState<ErpOrder | undefined | null>(undefined);
  const [formNFeVisible, setFormNFeVisible] = React.useState<boolean>(false);
  const [numberNF, setNumberNF] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(true);
  const [history, setHistory] = React.useState<HistoryByOrder[]>([]);

  const {
    toastService,
    alertService,
    getUpdatedErpOrderService,
    getErpNfService,
    getErpOrderNFeService,
    getHistoryByOrderService
  } = React.useContext(ServicesContext);

  React.useEffect(() => {
    if (!visible) return;
    getErpOrder();
  }, [visible]);

  const body = React.useMemo(() => {
    if (!visible) return null;

    if (loading) return <WaitingBox />

    if (formNFeVisible) {
      return (
        <Field
          type={FieldType.Text}
          label={'Informe o número da nota fiscal (incluir zeros à esquerda, conforme está no Omie)'}
          value={numberNF}
          change={(value) => setNumberNF(value)}
        />
      )
    }

    if (!erpOrder || !erpOrder.erpNfes || erpOrder.erpNfes.length === 0) {
      return (
        <Alert variant='warning' style={{ marginBottom: '0px' }}>
          Nenhuma nota fiscal encontrada.
        </Alert>
      );
    }

    return erpOrder.erpNfes.map((nfe, index) => {
      const nfeHistory = history.filter(h => {
        const searchValue = 'NFs depois:';
        let startPosition = h.details.indexOf(searchValue);
        if (startPosition < 0) return false;
        startPosition += searchValue.length;
        const nfs = h.details.substring(startPosition)?.trim()?.split(',');
        return nfs?.length > 0 && nfs.some(nf => nf.trim() == nfe.nfeNumber);
      });
      const firstNfeHistory = nfeHistory.length > 0 ? nfeHistory[0] : null;
      return (
        <ListItem borderTop={index > 0} key={`item-${index}`}>
          <NfBlock>
            <LeftContainer>
              <Title>{nfe.nfeNumber}</Title>
              <Details>{nfe.keyNfe}</Details>
            </LeftContainer>
            <RightContainer>
              <CreatedAt>{nfe.issuedAtDate + ' ' + nfe.issuedAtTime}</CreatedAt>
              {nfe.isCancelled ? <Status>{'Cancelada'}</Status> : null}
            </RightContainer>
          </NfBlock>
          {firstNfeHistory ? (
            <HistoryBlock>
              Adicionada <strong>{moment(firstNfeHistory.createdAt).format('DD/MM/yyyy HH:mm:ss')}</strong> após localização e 
              alteração por <strong>{firstNfeHistory.userName}</strong>.
            </HistoryBlock>
          ) : null}
        </ListItem>
      )
    });
  }, [visible, erpOrder, formNFeVisible, numberNF, loading, history]);

  const memoFooter = React.useMemo(() => {
    if (!visible || loading) return null;

    const isEcommerceOrAdmin = session?.user?.profileType === ProfileType.Ecommerce || session?.user?.profileType === ProfileType.Admin;

    if (!isEcommerceOrAdmin) return null;

    let buttons = null;

    if (formNFeVisible) {
      buttons = (
        <>
          <Button text={'Cancelar'} marginRight='20px' click={() => cancelFormNFe()} />
          <Button text={'Confirmar'} click={() => getNFe()} />
        </>
      );
    }

    if (!buttons) {
      buttons = (
        <>
          <Button text={'Atualizar ou obter nota fiscal'} click={() => startFormNFe()} />
        </>
      );
    }

    if (buttons) {
      return (
        <Modal.Footer>
          <FooterContent>
            {buttons}
          </FooterContent>
        </Modal.Footer>
      );
    }

    return null;
  }, [visible, erpOrder, formNFeVisible, numberNF, loading, history]);

  if (!visible) return null;

  return (
    <Modal
      show={visible}
      onHide={() => clickCloseModal()}
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body id='modalListNFesBody' style={{ maxHeight: '78vh', overflowY: 'auto' }}>
        {body}
      </Modal.Body>

      {memoFooter}
    </Modal>
  );

  function clickCloseModal() {
    close(orderId);
  }

  function startFormNFe() {
    setNumberNF('');
    setFormNFeVisible(true);
  }

  function cancelFormNFe() {
    setNumberNF('');
    setFormNFeVisible(false);
  }

  function getErpOrder() {
    if (!orderId) return;
    setLoading(true);
    getUpdatedErpOrderService
      .handle({
        orderId: orderId,
        userId: session?.user?.id
      }).then(result => {
        setErpOrder(result?.value?.erpOrder);
        getHistoryByOrderService.handle({ orderId: orderId })
          .then(hresult => {
            const nfeHistory = (hresult?.value?.history || []).filter(h => h.stateDescription.includes('Localização e alteração de NFe por Usuário'));
            setHistory(nfeHistory);
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch(() => {
        setLoading(false);
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
      });
  }

  async function getNFe() {
    if (!orderId) return;

    if (!numberNF) {
      toastService.warning('Informe o número da nota fiscal');
      return;
    }

    const getNfResult = await getErpNfService.handle({ number: numberNF });

    const erpOrderId = getNfResult?.value?.nf?.complement?.orderId;
    const erpNfeKey = getNfResult?.value?.nf?.complement?.nfeKey;
    const erpNfValue = getNfResult?.value?.nf?.total?.icmsTotal?.nfValue;

    if (!erpOrderId || !erpNfeKey || erpNfValue === undefined || erpNfValue === null) {
      toastService.warning('Nota fiscal não encontrada.');
      return;
    }

    const message = `Nota fiscal encontrada: <br>Chave: ${erpNfeKey} <br>Valor: ${erpNfValue.toFixed(2)} <br>Confirma a nota fiscal?`;
    const confirmResult = await alertService.confirmHtml(message);

    if (!confirmResult.isConfirmed) {
      cancelFormNFe();
      return;
    }

    const input = new GetErpOrderNFeInput();
    input.orderId = orderId;
    input.erpOrderCode = erpOrderId;
    input.collectOrder = false;
    input.userId = session?.user?.id;
    const getErpOrderResult = await getErpOrderNFeService.handle(input);

    if (!getErpOrderResult?.value?.result) {
      alertService.warning('Não foi possível obter e salvar ou atualizar a nota fiscal. Aguarde alguns segundos, tente novamente e se o problema persistir após várias tentativas, avise o time Tech.');
      return;
    }

    toastService.info('Nota fiscal salva ou atualizada com sucesso.');

    cancelFormNFe();
    getErpOrder();
  }
}

export { ModalListNFes };